/**
 * Blog Post: How to Use Code Library
 * Author: Technical Expert (Alex Chen)
 */

import type { BlogPost } from "../types";

export const howToUseCodeLibraryPost: BlogPost = {
  slug: "how-to-use-code-library",
  title: "Mastering the Code Library: Search NBC, NPC, NFC, and Calgary Bylaws",
  description:
    "A technical guide to searching and navigating the complete building code database, including NBC 2023 Alberta Edition, National Plumbing Code, Fire Code, and Calgary Land Use Bylaw.",
  excerpt:
    "Learn how to efficiently search across multiple building codes and find the exact articles you need for your projects.",
  author: "technical-expert",
  category: "building-codes",
  featuredImage: "/blog/code-library-hero.jpg",
  tags: ["code library", "NBC", "NPC", "NFC", "Calgary bylaw", "search tips"],
  publishedAt: "2026-01-26",
  readingTime: 6,
  tableOfContents: [
    { id: "codes-available", title: "Building Codes Available", level: 2 },
    { id: "search-techniques", title: "Effective Search Techniques", level: 2 },
    {
      id: "understanding-code-structure",
      title: "Understanding Code Structure",
      level: 2,
    },
    { id: "common-searches", title: "Common Searches by Topic", level: 2 },
  ],
  callToAction: {
    title: "Start exploring",
    description: "Search the complete building code database.",
    buttonText: "Open Code Library",
    buttonLink: "/explore",
  },
  content: `
The Code Library is your comprehensive resource for building code research. Whether you're looking up a specific article number or searching for requirements on a particular topic, this tool gives you direct access to the codes that govern construction in Calgary and Alberta.

## Building Codes Available

Our database includes the following codes, fully searchable:

**NBC(AE) 2023 - National Building Code of Canada, Alberta Edition**
The primary building code for Alberta. Part 9 covers housing and small buildings (most residential projects). Parts 3-8 cover larger and commercial buildings.

**NPC 2020 - National Plumbing Code**
Requirements for plumbing systems including fixtures, drainage, venting, and water supply.

**NFC(AE) 2023 - National Fire Code of Canada, Alberta Edition**
Fire safety requirements for buildings, emergency systems, and hazardous materials storage.

**NECB 2020 - National Energy Code for Buildings**
Energy efficiency requirements including insulation, HVAC efficiency, and building envelope performance.

**Calgary Land Use Bylaw 1P2007**
Zoning districts, setbacks, height restrictions, parking requirements, and permitted uses for Calgary properties.

## Effective Search Techniques

**Search by Article Number**

If you know the specific article (e.g., "9.8.4.1"), enter it directly. The search will find that article and related sections.

**Search by Topic**

Use descriptive terms like:
- "stair width" - finds all stair-related requirements
- "egress window" - finds emergency escape requirements
- "fire separation garage" - finds fire rating requirements for garages

**Search by Code Section**

Filter your search to a specific code:
- Select "NBC Part 9" for residential building requirements
- Select "NPC" for plumbing-specific searches
- Select "Calgary LUB" for zoning questions

**Use Specific Terms**

Building codes use precise terminology. Some helpful terms:

- **Habitable room** - rooms used for living, sleeping, eating, or cooking
- **Service room** - rooms for building equipment (mechanical, electrical)
- **Egress** - emergency exit or escape route
- **Fire separation** - construction that prevents fire spread
- **Setback** - required distance from property line

## Understanding Code Structure

**NBC Structure:**

\`\`\`
Part 9 - Housing and Small Buildings
  Section 9.8 - Stairs, Ramps and Handrails
    Subsection 9.8.4 - Dimensions
      Article 9.8.4.1 - Stair Width
\`\`\`

Articles are cited as **9.8.4.1** - Part.Section.Subsection.Article

**Calgary Land Use Bylaw Structure:**

\`\`\`
Part 5 - Land Use Districts
  Division 5.1 - Residential Districts
    Section 110 - R-C1 Residential - Contextual One Dwelling
\`\`\`

## Common Searches by Topic

**Residential Stairs (NBC Part 9)**
- Article 9.8.4.1 - Stair width (min 860mm)
- Article 9.8.4.2 - Riser and tread dimensions
- Article 9.8.7.1 - Handrails

**Egress Requirements (NBC Part 9)**
- Article 9.9.10.1 - Egress windows from bedrooms
- Article 9.9.1.1 - Means of egress general
- Article 9.9.9.1 - Exit doors

**Fire Separation (NBC Part 9)**
- Article 9.10.9.18 - Fire separation between garage and dwelling
- Article 9.10.9.6 - Fire separation between dwelling units
- Article 9.10.8.1 - Fire resistance ratings

**Room Dimensions (NBC Part 9)**
- Article 9.5.3.1 - Ceiling height (min 2.3m habitable)
- Article 9.5.2.1 - Minimum room dimensions
- Article 9.5.6.1 - Bathroom and water closet rooms

**Calgary Zoning**
- Section 110 - R-C1 district rules
- Section 120 - R-C2 district rules
- Section 137 - R-CG (rowhouse/townhouse) district rules

When you find an article, click to view the full text including any tables, exceptions, and cross-references. You can copy the text for your documentation or notes.

Remember: Building codes are updated periodically. Our database reflects the current editions, but always confirm requirements with your local authority having jurisdiction (AHJ) for your specific project.
`,
};
